import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations, servicesData, teamData, statsData, contactInfo } from '../data/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import {
  FileText,
  MessageSquare,
  Building2,
  Scale,
  MessageCircle,
  Shield,
  User,
  MapPin,
  Phone,
  Mail,
  Globe,
  CheckCircle,
  Zap,
  Lock,
  Users,
  Newspaper,
  BookOpen,
  Menu
} from 'lucide-react';

const Home = () => {
  const { language, toggleLanguage } = useLanguage();
  const t = translations[language];
  const services = servicesData[language];
  const team = teamData[language];
  const contact = contactInfo[language];
  const { toast } = useToast();

  const [selectedService, setSelectedService] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    message: ''
  });

  const serviceIcons = {
    contracts: FileText,
    consultation: MessageSquare,
    company: Building2,
    litigation: Scale,
    arbitration: MessageCircle,
    risk: Shield,
    training: Users
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      toast({
        title: language === 'ar' ? 'خطأ' : 'Error',
        description: language === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields',
        variant: 'destructive'
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: language === 'ar' ? 'خطأ' : 'Error',
        description: language === 'ar' ? 'يرجى إدخال بريد إلكتروني صحيح' : 'Please enter a valid email address',
        variant: 'destructive'
      });
      return;
    }

    // Save to localStorage (mock backend)
    const submissions = JSON.parse(localStorage.getItem('contactSubmissions') || '[]');
    submissions.push({ ...formData, timestamp: new Date().toISOString() });
    localStorage.setItem('contactSubmissions', JSON.stringify(submissions));

    toast({
      title: language === 'ar' ? 'تم الإرسال بنجاح!' : 'Submitted Successfully!',
      description: language === 'ar' 
        ? `شكراً لك ${formData.name}! سيتم التواصل معك قريباً.`
        : `Thank you ${formData.name}! We will contact you soon.`
    });

    setFormData({
      name: '',
      email: '',
      phone: '',
      serviceType: '',
      message: ''
    });
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="nav-modern fixed w-full top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="logo-container">
                <img 
                  src="https://i.imgur.com/1efIyiz.jpeg" 
                  alt="Tamkeen Logo" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
                <div className="logo-fallback" style={{ display: 'none' }}>ت</div>
              </div>
              <div>
                <h1 className="text-2xl font-black text-gray-800 tracking-tight">
                  {language === 'ar' ? 'شركة التمكين' : 'Tamkeen Company'}
                </h1>
                <p className="text-sm text-gray-600 font-medium">
                  {t.footer.tagline}
                </p>
              </div>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-8">
              <a href="#home" onClick={(e) => { e.preventDefault(); scrollToSection('home'); }} className="nav-link">{t.nav.home}</a>
              <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="nav-link">{t.nav.services}</a>
              <a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }} className="nav-link">{t.nav.about}</a>
              <a href="#team" onClick={(e) => { e.preventDefault(); scrollToSection('team'); }} className="nav-link">{t.nav.team}</a>
              <a href="/magazine" className="nav-link">{t.nav.magazine}</a>
              <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="nav-link">{t.nav.contact}</a>
              <Button onClick={toggleLanguage} variant="outline" size="sm" className="language-switcher">
                <Globe className="w-4 h-4 mr-2" />
                {language === 'ar' ? 'EN' : 'عربي'}
              </Button>
            </div>
            
            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center gap-2">
              <Button onClick={toggleLanguage} variant="outline" size="sm">
                <Globe className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm">
                <Menu className="w-6 h-6" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="gradient-bg min-h-screen flex items-center relative overflow-hidden pt-20">
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div className="text-white fade-in">
              <h1 className="text-5xl lg:text-6xl font-black leading-tight mb-6">
                {t.hero.title}
                <span className="text-yellow-400 block">{t.hero.titleHighlight}</span>
                {t.hero.titleEnd}
              </h1>
              <p className="text-xl text-slate-200 mb-8 leading-relaxed">
                {t.hero.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button onClick={() => scrollToSection('contact')} className="btn-secondary-modern text-lg px-8 py-6">
                  {t.hero.startNow}
                </Button>
                <Button onClick={() => scrollToSection('services')} variant="outline" className="glass-effect text-white border-white/20 hover:bg-white/20 text-lg px-8 py-6">
                  {t.hero.ourServices}
                </Button>
              </div>
            </div>
            
            {/* Visual Elements */}
            <div className="slide-in">
              <div className="modern-card p-12 text-center">
                <div className="w-32 h-32 mx-auto mb-8 rounded-full overflow-hidden border-4 border-yellow-400 shadow-2xl">
                  <img 
                    src="https://i.imgur.com/1efIyiz.jpeg" 
                    alt="Tamkeen Logo" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                  <div className="logo-fallback-large" style={{ display: 'none' }}>ت</div>
                </div>
                
                <div className="grid grid-cols-2 gap-6 mb-8">
                  <div className="feature-card bg-slate-50">
                    <div className="feature-icon bg-slate-700">
                      <CheckCircle className="w-6 h-6 text-yellow-400" />
                    </div>
                    <h4 className="font-bold text-gray-800 mb-1">{t.hero.trustedExperience}</h4>
                    <p className="text-sm text-gray-600">{t.hero.yearsExperience}</p>
                  </div>
                  
                  <div className="feature-card bg-yellow-50">
                    <div className="feature-icon bg-yellow-600">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="font-bold text-gray-800 mb-1">{t.hero.quickSolutions}</h4>
                    <p className="text-sm text-gray-600">{t.hero.instantResponse}</p>
                  </div>
                  
                  <div className="feature-card bg-green-50">
                    <div className="feature-icon bg-green-600">
                      <Lock className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="font-bold text-gray-800 mb-1">{t.hero.fullPrivacy}</h4>
                    <p className="text-sm text-gray-600">{t.hero.dataProtection}</p>
                  </div>
                  
                  <div className="feature-card bg-purple-50">
                    <div className="feature-icon bg-purple-600">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="font-bold text-gray-800 mb-1">{t.hero.specializedTeam}</h4>
                    <p className="text-sm text-gray-600">{t.hero.legalExperts}</p>
                  </div>
                </div>
                
                <Button onClick={() => scrollToSection('contact')} className="btn-primary-modern text-lg px-8 py-4 w-full">
                  {t.hero.bookConsultation}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white relative">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {statsData.map((stat, index) => (
              <div key={index} className="text-center fade-in" style={{ animationDelay: `${index * 0.2}s` }}>
                <div className="stats-counter mb-2">{stat.number}</div>
                <p className="text-gray-600 font-medium">{t.stats[stat.key]}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gradient-to-br from-gray-50 to-slate-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">{t.services.title}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t.services.description}
            </p>
            <div className="section-divider"></div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const Icon = serviceIcons[service.id];
              return (
                <div key={service.id} className="modern-card p-8 floating-card text-center">
                  <div className="service-icon-modern">
                    <Icon className="w-8 h-8 text-yellow-400" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  <Button 
                    onClick={() => setSelectedService(service)}
                    className="btn-primary-modern"
                  >
                    {t.services.learnMore}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Details Dialog */}
      <Dialog open={!!selectedService} onOpenChange={() => setSelectedService(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">{selectedService?.details.title}</DialogTitle>
            <DialogDescription className="text-base">
              {selectedService?.details.description}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-6">
            <h4 className="font-bold text-lg mb-4">
              {language === 'ar' ? 'الخدمات المتاحة:' : 'Available Services:'}
            </h4>
            <ul className="space-y-2">
              {selectedService?.details.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </DialogContent>
      </Dialog>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="fade-in">
              <h2 className="text-4xl font-bold text-gray-800 mb-6">{t.about.title}</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {t.about.description1}
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {t.about.description2}
              </p>
              
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <div className="text-2xl font-bold text-slate-600 mb-2">500+</div>
                  <p className="text-gray-600">{t.about.successfulCases}</p>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-xl">
                  <div className="text-2xl font-bold text-yellow-600 mb-2">98%</div>
                  <p className="text-gray-600">{t.about.successRate}</p>
                </div>
              </div>
              
              <Button onClick={() => scrollToSection('team')} className="btn-secondary-modern">
                {t.about.learnMoreAboutUs}
              </Button>
            </div>
            
            <div className="slide-in">
              <div className="modern-card p-8">
                <div className="w-32 h-32 mx-auto mb-6 rounded-full overflow-hidden border-4 border-yellow-400">
                  <img 
                    src="https://i.imgur.com/1efIyiz.jpeg" 
                    alt="Tamkeen Logo" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                  <div className="logo-fallback-large" style={{ display: 'none' }}>ت</div>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">{t.about.ourVision}</h3>
                <p className="text-gray-600 text-center leading-relaxed">
                  {t.about.visionText}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="py-20 bg-gradient-to-br from-slate-50 to-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">{t.team.title}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t.team.description}
            </p>
            <div className="section-divider"></div>
          </div>
          
          {/* Featured Lawyer - Ahmed Al-Jubouri */}
          <div className="flex justify-center">
            {team.filter(member => member.featured).map((member) => (
              <div key={member.id} className="modern-card p-12 text-center floating-card team-featured max-w-lg w-full">
                {member.image ? (
                  <div className="w-48 h-48 mx-auto mb-8 rounded-full overflow-hidden border-4 border-yellow-400 shadow-xl">
                    <img 
                      src={member.image} 
                      alt={member.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                    <div className="service-icon-modern" style={{ display: 'none' }}>
                      <User className="w-8 h-8 text-yellow-400" />
                    </div>
                  </div>
                ) : (
                  <div className="service-icon-modern">
                    <User className="w-8 h-8 text-yellow-400" />
                  </div>
                )}
                <h3 className="text-3xl font-bold text-gray-800 mb-3">{member.name}</h3>
                <p className="text-lg font-semibold text-yellow-600 mb-4">{member.position}</p>
                <p className="text-gray-600 text-lg">{member.experience}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Magazine Section */}
      <section id="magazine" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">{t.magazine.title}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t.magazine.description}
            </p>
            <div className="section-divider"></div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-12">
            <div className="news-card bg-slate-50">
              <div className="news-icon bg-slate-600">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-gray-800 mb-2">{t.magazine.legalAnalysis}</h3>
              <p className="text-gray-600 text-sm">{t.magazine.legalAnalysisDesc}</p>
            </div>
            
            <div className="news-card bg-yellow-50">
              <div className="news-icon bg-yellow-600">
                <Newspaper className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-gray-800 mb-2">{t.magazine.newsLegislation}</h3>
              <p className="text-gray-600 text-sm">{t.magazine.newsLegislationDesc}</p>
            </div>
          </div>

          <div className="text-center">
            <a href="/magazine">
              <Button className="btn-secondary-modern text-lg px-8 py-4">
                {language === 'ar' ? 'زيارة المجلة' : 'Visit Magazine'}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 gradient-bg">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">{t.contact.title}</h2>
            <p className="text-xl text-slate-200 max-w-3xl mx-auto">
              {t.contact.description}
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="glass-effect rounded-3xl p-8">
              <h3 className="text-2xl font-bold text-white mb-6">{t.contact.contactUs}</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <Input
                  type="text"
                  name="name"
                  placeholder={t.contact.fullName}
                  value={formData.name}
                  onChange={handleInputChange}
                  className="input-glass"
                  required
                />
                <Input
                  type="email"
                  name="email"
                  placeholder={t.contact.email}
                  value={formData.email}
                  onChange={handleInputChange}
                  className="input-glass"
                  required
                />
                <Input
                  type="tel"
                  name="phone"
                  placeholder={t.contact.phone}
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="input-glass"
                  required
                />
                <Input
                  type="text"
                  name="serviceType"
                  placeholder={language === 'ar' ? 'اكتب نوع العقد' : 'Type the contract type'}
                  value={formData.serviceType}
                  onChange={handleInputChange}
                  className="input-glass"
                />
                <Textarea
                  name="message"
                  rows={4}
                  placeholder={t.contact.description_field}
                  value={formData.message}
                  onChange={handleInputChange}
                  className="input-glass resize-none"
                  required
                />
                <Button type="submit" className="btn-secondary-modern w-full text-lg py-6">
                  {t.contact.submit}
                </Button>
              </form>
            </div>
            
            {/* Contact Info */}
            <div className="space-y-8">
              <div className="glass-effect rounded-3xl p-6">
                <div className="flex items-center gap-4">
                  <div className="contact-icon bg-yellow-400">
                    <MapPin className="w-6 h-6 text-slate-800" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold">{t.contact.address}</h4>
                    <p className="text-slate-200">{contact.address}</p>
                  </div>
                </div>
              </div>
              
              <div className="glass-effect rounded-3xl p-6">
                <div className="flex items-center gap-4">
                  <div className="contact-icon bg-yellow-400">
                    <Phone className="w-6 h-6 text-slate-800" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold">{t.contact.phoneNumber}</h4>
                    <p className="text-slate-200">{contact.phone}</p>
                  </div>
                </div>
              </div>
              
              <div className="glass-effect rounded-3xl p-6">
                <div className="flex items-center gap-4">
                  <div className="contact-icon bg-yellow-400">
                    <Mail className="w-6 h-6 text-slate-800" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold">{t.contact.emailAddress}</h4>
                    <p className="text-slate-200">{contact.email}</p>
                  </div>
                </div>
              </div>
              
              <div className="glass-effect rounded-3xl p-6">
                <div className="flex items-center gap-4">
                  <div className="contact-icon bg-yellow-400">
                    <Globe className="w-6 h-6 text-slate-800" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold">{t.contact.socialMedia}</h4>
                    <p className="text-slate-200">{contact.social}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Download App Section */}
      <section className="py-16 bg-gradient-to-r from-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-yellow-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-72 h-72 bg-yellow-600 rounded-full filter blur-3xl"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-start">
              <h2 className="text-3xl font-bold text-white mb-3">
                {language === 'ar' ? 'المفكرة القانونية' : 'Legal Notebook'}
              </h2>
              <p className="text-slate-300 text-lg">
                {language === 'ar' 
                  ? 'كل ما يحتاجه المحامي في مكان واحد'
                  : 'Everything a lawyer needs in one place'}
              </p>
            </div>
            <a 
              href="https://legaltamkeen.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              data-testid="visit-website-btn"
            >
              <Button className="btn-secondary-modern text-lg px-10 py-6 flex items-center gap-3">
                <BookOpen className="w-5 h-5" />
                {language === 'ar' ? 'افتح المفكرة' : 'Open Notebook'}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="logo-container">
                  <img 
                    src="https://i.imgur.com/1efIyiz.jpeg" 
                    alt="Tamkeen Logo" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                  <div className="logo-fallback" style={{ display: 'none' }}>ت</div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{language === 'ar' ? 'شركة التمكين' : 'Tamkeen Company'}</h3>
                  <p className="text-sm text-slate-300">{t.footer.tagline}</p>
                </div>
              </div>
              <p className="text-slate-300 leading-relaxed">
                {t.footer.description}
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-bold mb-6">{t.footer.quickLinks}</h4>
              <ul className="space-y-3">
                <li><a href="#home" onClick={(e) => { e.preventDefault(); scrollToSection('home'); }} className="footer-link">{t.nav.home}</a></li>
                <li><a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="footer-link">{t.nav.services}</a></li>
                <li><a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }} className="footer-link">{t.nav.about}</a></li>
                <li><a href="#team" onClick={(e) => { e.preventDefault(); scrollToSection('team'); }} className="footer-link">{t.nav.team}</a></li>
                <li><a href="/magazine" className="footer-link">{t.nav.magazine}</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-bold mb-6">{t.footer.ourServices}</h4>
              <ul className="space-y-3">
                {services.slice(0, 5).map(service => (
                  <li key={service.id}>
                    <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="footer-link">
                      {service.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-bold mb-6">{t.footer.contactInfo}</h4>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-yellow-400" />
                  <span className="text-slate-300 text-sm">{contact.address}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-yellow-400" />
                  <span className="text-slate-300 text-sm">{contact.phone}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-yellow-400" />
                  <span className="text-slate-300 text-sm">{contact.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-yellow-400" />
                  <span className="text-slate-300 text-sm">{contact.social}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-slate-700 mt-12 pt-8 text-center">
            <p className="text-slate-400">
              {t.footer.copyright}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
